import { z } from 'zod';
import { optionTypeSchema } from '../../../helper';
import { projectSchema, IProject } from './project';
import { ICommodity } from '../commodity/commodity';

/**
 * Schema for validating project form data.
 * Extends the base project schema with form-specific validations.
 *
 * @property {string} [number] - Project number (max 255 characters)
 * @property {string} [description] - Project description (max 255 characters)
 * @property {OptionType} [commodity] - Commodity selection with label/value pair
 * @property {boolean} [isImported] - Whether the project is imported
 * @property {Date} [endedOn] - Optional date when the project ended
 */
export const projectFormSchema = projectSchema.extend({
    commodity: optionTypeSchema.refine(val => val !== undefined, { message: 'Commodity is required' }),
}).omit({
    commodityId: true,
});

/**
 * Default schema for project form initialization.
 * Extends the form schema by making required fields optional with defaults.
 *
 * @property {string} [number] - Optional project number
 * @property {string} [description] - Optional project description
 * @property {OptionType} [commodity] - Optional commodity selection with label/value pair
 */
export const projectDefaultFormSchema = projectFormSchema.extend({
    number: z.string().optional().default(''),
    description: z.string().optional().default(''),
    commodity: optionTypeSchema.optional(),
});

/**
 * TypeScript interface for project default form values.
 * Generated from the project default form schema for type safety.
 */
export type IProjectDefaultFormValues = z.infer<typeof projectDefaultFormSchema>;

/**
 * TypeScript interface for project form values.
 * Generated from the project form schema for type safety.
 */
export type IProjectFormValues = z.infer<typeof projectFormSchema>;

/**
 * Converts a Project model to form values for editing.
 * If no project is provided, returns default form values.
 *
 * @param {IProject} [project] - Optional project model to convert
 * @param {Array<ICommodity>} [commodities] - Array of commodities to use for mapping
 * @returns {IProjectFormValues | IProjectDefaultFormValues} Form values ready for editing
 */
export const ProjectFormValues = (
    project ?: IProject,
    commodities ?: Array<ICommodity>
) : IProjectFormValues | IProjectDefaultFormValues => {
    if (!project) return projectDefaultFormSchema.parse({});

    const { commodityId, endedOn } = project;

    return projectFormSchema.parse({
        ...project,
        commodity: commodities?.find(x => x.id === commodityId)
            ? {
                label: commodities?.find(x => x.id === commodityId)?.name ?? '',
                value: commodityId,
            }
            : undefined,
        endedOn: endedOn ? new Date(endedOn) : undefined,
    });
};

/**
 * Validates project form values against the schema.
 * Logs validation errors to console if validation fails.
 *
 * @param {IProjectFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation succeeds, false otherwise
 */
export const validateProjectForm = (formValues : IProjectFormValues) : boolean => {
    try {
        projectFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Project form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
